import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import { API } from "@/contexts/AuthContext";
import { MapPin, Plus, Minus, Maximize2, Hand, ImageIcon, X, ChevronRight } from "lucide-react";

/* Status → color mapping (rule set from user)
   Occupied (has owner) → Blue
   Claimed  (owner verified via OTP) → Green
   Vacant   (no owner) → Amber
   Missing  (no unit data) → Grey                                         */
const STATUS_COLORS = {
  occupied: { fill: "rgba(37,99,235,0.28)",  stroke: "#2563EB", label: "Occupied", dot: "#2563EB" },
  claimed:  { fill: "rgba(16,185,129,0.30)", stroke: "#059669", label: "Claimed",  dot: "#059669" },
  vacant:   { fill: "rgba(245,158,11,0.30)", stroke: "#D97706", label: "Vacant",   dot: "#D97706" },
  missing:  { fill: "rgba(113,113,122,0.22)",stroke: "#71717A", label: "Missing",  dot: "#71717A" },
};

const MIN_SCALE = 1;
const MAX_SCALE = 5;

export default function FloorMapViewer({ floor, wing, units, selectedUnit, onUnitClick }) {
  const [hotspots, setHotspots]        = useState([]);
  const [floorPlanUrl, setFloorPlanUrl]= useState(null);
  const [planAspect, setPlanAspect]    = useState(null); // w/h ratio of original plan
  const [refExpanded, setRefExpanded]  = useState(false); // reference lightbox
  // Reference thumbnail: default collapsed on small screens so it doesn't cover polygons
  const [refCollapsed, setRefCollapsed]= useState(
    typeof window !== "undefined" && window.innerWidth < 768
  );
  const [hoveredUnit, setHoveredUnit]  = useState(null);
  const [tip, setTip]                  = useState({ show: false, x: 0, y: 0, unit: null });
  const [transform, setTransform]      = useState({ scale: 1, tx: 0, ty: 0 });
  const [isPanning, setIsPanning]      = useState(false);
  const panStart                       = useRef({ x: 0, y: 0, tx: 0, ty: 0 });
  const viewportRef                    = useRef(null);
  const canvasRef                      = useRef(null);
  // Touch/pointer gesture state (pan + pinch-to-zoom)
  const activePointers                 = useRef(new Map()); // pointerId -> {x, y}
  const pinchStart                     = useRef(null); // {dist, scale, cx, cy, tx, ty}
  const tapStart                       = useRef(null); // {x, y, t}

  // Re-collapse when crossing the mobile breakpoint on resize
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth < 768) setRefCollapsed(true);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const unitsById = useMemo(
    () => Object.fromEntries(units.map((u) => [u.unit_id, u])),
    [units]
  );

  useEffect(() => {
    const params = new URLSearchParams({ floor });
    if (wing !== undefined) params.append("wing", wing);
    axios.get(`${API}/hotspots?${params}`)
      .then((r) => setHotspots(r.data))
      .catch(() => setHotspots([]));

    setPlanAspect(null);
    axios.get(`${API}/floor-plans`)
      .then((r) => {
        const plan = r.data.find((p) => p.floor === floor && p.wing === wing);
        if (plan) {
          const url = `${API}/files/${plan.storage_path}`;
          setFloorPlanUrl(url);
          // Preload to capture aspect ratio for the polygon canvas
          const img = new Image();
          img.onload = () => setPlanAspect(img.naturalWidth / img.naturalHeight);
          img.src = url;
        } else {
          setFloorPlanUrl(null);
        }
      })
      .catch(() => setFloorPlanUrl(null));
  }, [floor, wing]);

  useEffect(() => { setTransform({ scale: 1, tx: 0, ty: 0 }); }, [floor, wing]);

  const getStatus = (unitId) => {
    const u = unitsById[unitId];
    if (!u) return "missing";
    return u.directory_status || "vacant";
  };

  const getOwnerName = (unitId) => {
    const u = unitsById[unitId];
    if (!u || !u.owners?.length) return "—";
    return u.owners[0].name;
  };

  const getSqFt = (unitId) => {
    const u = unitsById[unitId];
    if (!u || !u.size_sqft) return "—";
    return `${u.size_sqft.toFixed(0)} sq ft`;
  };

  /* ─── Pan + Zoom controls ─────────────────────────────────────── */
  const clampTransform = useCallback((t) => {
    if (!viewportRef.current || !canvasRef.current) return t;
    const vp = viewportRef.current.getBoundingClientRect();
    const cw = canvasRef.current.offsetWidth  * t.scale;
    const ch = canvasRef.current.offsetHeight * t.scale;
    const maxX = Math.max(0, (cw - vp.width) / 2 + 100);
    const maxY = Math.max(0, (ch - vp.height) / 2 + 100);
    return {
      ...t,
      tx: Math.max(-maxX, Math.min(maxX, t.tx)),
      ty: Math.max(-maxY, Math.min(maxY, t.ty)),
    };
  }, []);

  const zoomBy = (delta, focus = null) => {
    setTransform((prev) => {
      const nextScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, prev.scale + delta));
      if (nextScale === prev.scale) return prev;
      const vp = viewportRef.current?.getBoundingClientRect();
      const fx = focus?.x ?? (vp ? vp.width / 2 : 0);
      const fy = focus?.y ?? (vp ? vp.height / 2 : 0);
      const dx = fx - (vp?.width ?? 0) / 2;
      const dy = fy - (vp?.height ?? 0) / 2;
      const ratio = nextScale / prev.scale;
      const nextTx = (prev.tx - dx) * ratio + dx;
      const nextTy = (prev.ty - dy) * ratio + dy;
      return clampTransform({ scale: nextScale, tx: nextTx, ty: nextTy });
    });
  };

  const handleZoomIn  = () => zoomBy(0.4);
  const handleZoomOut = () => zoomBy(-0.4);
  const handleReset   = () => setTransform({ scale: 1, tx: 0, ty: 0 });

  const handleWheel = (e) => {
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    const vp   = viewportRef.current.getBoundingClientRect();
    const focus= { x: e.clientX - vp.left, y: e.clientY - vp.top };
    zoomBy(e.deltaY > 0 ? -0.2 : 0.2, focus);
  };

  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    setIsPanning(true);
    panStart.current = { x: e.clientX, y: e.clientY, tx: transform.tx, ty: transform.ty };
  };

  const handleMouseMove = (e) => {
    if (isPanning) {
      setTransform((t) => clampTransform({
        ...t,
        tx: panStart.current.tx + (e.clientX - panStart.current.x),
        ty: panStart.current.ty + (e.clientY - panStart.current.y),
      }));
    }
    if (tip.show) {
      const vp = viewportRef.current.getBoundingClientRect();
      setTip((prev) => ({ ...prev, x: e.clientX - vp.left, y: e.clientY - vp.top }));
    }
  };

  const handleMouseUp = () => setIsPanning(false);

  /* ─── Touch / pointer gestures: pan + pinch ──────────────────── */
  const setTransformClamped = useCallback((updater) => {
    setTransform((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      return clampTransform(next);
    });
  }, [clampTransform]);

  const handlePointerDown = (e) => {
    // Only handle touch/pen here — mouse uses onMouseDown flow above
    if (e.pointerType === "mouse") return;
    e.preventDefault();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* noop */ }
    activePointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (activePointers.current.size === 1) {
      // Start pan
      setIsPanning(true);
      panStart.current = { x: e.clientX, y: e.clientY, tx: transform.tx, ty: transform.ty };
      tapStart.current = { x: e.clientX, y: e.clientY, t: Date.now() };
      pinchStart.current = null;
    } else if (activePointers.current.size === 2) {
      // Start pinch
      const pts = Array.from(activePointers.current.values());
      const dx = pts[0].x - pts[1].x;
      const dy = pts[0].y - pts[1].y;
      const dist = Math.hypot(dx, dy) || 1;
      const vp = viewportRef.current?.getBoundingClientRect();
      const cx = (pts[0].x + pts[1].x) / 2 - (vp?.left ?? 0);
      const cy = (pts[0].y + pts[1].y) / 2 - (vp?.top ?? 0);
      pinchStart.current = {
        dist,
        scale: transform.scale,
        cx, cy,
        tx: transform.tx,
        ty: transform.ty,
      };
      setIsPanning(false);
      tapStart.current = null;
    }
  };

  const handlePointerMove = (e) => {
    if (e.pointerType === "mouse") return;
    if (!activePointers.current.has(e.pointerId)) return;
    activePointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (activePointers.current.size === 2 && pinchStart.current) {
      // Pinch zoom around focal point
      const pts = Array.from(activePointers.current.values());
      const dx = pts[0].x - pts[1].x;
      const dy = pts[0].y - pts[1].y;
      const dist = Math.hypot(dx, dy) || 1;
      const ratio = dist / pinchStart.current.dist;
      const nextScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, pinchStart.current.scale * ratio));
      const vp = viewportRef.current?.getBoundingClientRect();
      const vpW = vp?.width ?? 0;
      const vpH = vp?.height ?? 0;
      const fx = pinchStart.current.cx;
      const fy = pinchStart.current.cy;
      const dxF = fx - vpW / 2;
      const dyF = fy - vpH / 2;
      const scaleRatio = nextScale / pinchStart.current.scale;
      const nextTx = (pinchStart.current.tx - dxF) * scaleRatio + dxF;
      const nextTy = (pinchStart.current.ty - dyF) * scaleRatio + dyF;
      setTransformClamped({ scale: nextScale, tx: nextTx, ty: nextTy });
      tapStart.current = null;
      return;
    }

    if (activePointers.current.size === 1 && isPanning) {
      const p = Array.from(activePointers.current.values())[0];
      // Cancel tap if finger moved more than 8px
      if (tapStart.current) {
        const md = Math.hypot(p.x - tapStart.current.x, p.y - tapStart.current.y);
        if (md > 8) tapStart.current = null;
      }
      setTransformClamped((t) => ({
        ...t,
        tx: panStart.current.tx + (p.x - panStart.current.x),
        ty: panStart.current.ty + (p.y - panStart.current.y),
      }));
    }
  };

  const handlePointerUp = (e) => {
    if (e.pointerType === "mouse") return;
    activePointers.current.delete(e.pointerId);
    if (activePointers.current.size < 2) pinchStart.current = null;
    if (activePointers.current.size === 0) {
      setIsPanning(false);
      tapStart.current = null;
    } else if (activePointers.current.size === 1) {
      // Convert remaining finger into a fresh pan
      const p = Array.from(activePointers.current.values())[0];
      panStart.current = { x: p.x, y: p.y, tx: transform.tx, ty: transform.ty };
      setIsPanning(true);
    }
  };

  /* ─── Two-way sync: zoom to selected unit ─────────────────────── */
  useEffect(() => {
    if (!selectedUnit || !viewportRef.current || !canvasRef.current) return;
    const hs = hotspots.find((h) => h.unit_id === selectedUnit);
    if (!hs || !hs.points?.length) return;

    const cx = hs.points.reduce((a, [x]) => a + x, 0) / hs.points.length;
    const cy = hs.points.reduce((a, [, y]) => a + y, 0) / hs.points.length;

    const targetScale = 2.2;
    const vp = viewportRef.current.getBoundingClientRect();
    const canvas = canvasRef.current;
    const px = (cx / 100) * canvas.offsetWidth;
    const py = (cy / 100) * canvas.offsetHeight;
    const tx = vp.width / 2 - px * targetScale;
    const ty = vp.height / 2 - py * targetScale;
    setTransform(clampTransform({ scale: targetScale, tx, ty }));
  }, [selectedUnit, hotspots, clampTransform]);

  const handlePolyEnter = (h, e) => {
    const vp = viewportRef.current.getBoundingClientRect();
    setHoveredUnit(h.unit_id);
    setTip({ show: true, x: e.clientX - vp.left, y: e.clientY - vp.top, unit: h.unit_id });
  };
  const handlePolyLeave = () => {
    setHoveredUnit(null);
    setTip({ show: false, x: 0, y: 0, unit: null });
  };

  const floorLabel = floor === "GF" ? "Ground Floor" : `First Floor · Block ${wing}`;

  return (
    <div className="relative w-full h-full flex flex-col" data-testid="floor-map-viewer">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between px-4 py-2 border-b border-[#E5E5E5] dark:border-[#272A30] bg-white/50 dark:bg-[#1A1D24]/50">
        <div className="flex items-center gap-2">
          <MapPin size={14} className="text-[#9C7C54]" />
          <span className="text-xs tracking-[0.15em] uppercase font-semibold text-[#737373] dark:text-[#A1A1AA]">
            {floorLabel}
          </span>
        </div>
        <div className="flex items-center gap-3 text-[10px] text-[#737373] flex-wrap">
          {Object.entries(STATUS_COLORS).map(([k, c]) => (
            <span key={k} className="flex items-center gap-1" data-testid={`legend-${k}`}>
              <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: c.dot }} />
              {c.label}
            </span>
          ))}
        </div>
      </div>

      {/* Map viewport */}
      <div
        ref={viewportRef}
        className="relative flex-1 overflow-hidden bg-[#F5F4F2] dark:bg-[#0F1115] select-none"
        style={{ cursor: isPanning ? "grabbing" : "grab", touchAction: "none" }}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => { handleMouseUp(); handlePolyLeave(); }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        data-testid="map-viewport"
      >
        <div
          ref={canvasRef}
          className="absolute polygon-canvas"
          style={{
            transform: `translate(-50%, -50%) translate(${transform.tx}px, ${transform.ty}px) scale(${transform.scale})`,
            transformOrigin: "center center",
            left: "50%", top: "50%",
            transition: isPanning ? "none" : "transform 260ms cubic-bezier(0.22, 1, 0.36, 1)",
            display: "block",
            // Preserve original plan aspect ratio so polygons stay proportional
            aspectRatio: planAspect ? String(planAspect) : "0.43",
            // Fit within the viewport at scale=1
            height: "min(100%, 95%)",
            width:  "auto",
            maxHeight: "95%",
            maxWidth:  "95%",
          }}
        >
          {/* SVG polygon overlay */}
          <svg
            className="absolute inset-0 w-full h-full"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            style={{ pointerEvents: "none" }}
          >
            {/* Subtle grid */}
            <defs>
              <pattern id="polyGrid" width="5" height="5" patternUnits="userSpaceOnUse">
                <path d="M 5 0 L 0 0 0 5" fill="none" stroke="#9C7C54" strokeOpacity="0.12" strokeWidth="0.08" />
              </pattern>
            </defs>
            <rect width="100" height="100" fill="url(#polyGrid)" />
            {hotspots.map((h) => {
              if (!h.points?.length) return null;
              const status = getStatus(h.unit_id);
              const c      = STATUS_COLORS[status] || STATUS_COLORS.missing;
              const isSel  = selectedUnit === h.unit_id;
              const isHov  = hoveredUnit === h.unit_id;
              const pts    = h.points.map(([x, y]) => `${x},${y}`).join(" ");
              const cx = h.points.reduce((a, [x]) => a + x, 0) / h.points.length;
              const cy = h.points.reduce((a, [, y]) => a + y, 0) / h.points.length;
              const fillOpacity = isSel ? 0.55 : isHov ? 0.5 : null;
              const fill = fillOpacity !== null
                ? c.fill.replace(/[\d.]+\)$/, `${fillOpacity})`)
                : c.fill;
              return (
                <g
                  key={h.unit_id}
                  data-testid={`hotspot-${h.unit_id}`}
                  style={{ pointerEvents: "auto", cursor: "pointer" }}
                  onClick={(e) => { e.stopPropagation(); onUnitClick(h.unit_id); }}
                  onMouseEnter={(e) => handlePolyEnter(h, e)}
                  onMouseLeave={handlePolyLeave}
                >
                  {isSel && (
                    <polygon
                      points={pts}
                      fill="none"
                      stroke={c.stroke}
                      strokeWidth={0.6}
                      className="unit-pulse"
                      vectorEffect="non-scaling-stroke"
                    />
                  )}
                  <polygon
                    points={pts}
                    fill={fill}
                    stroke={c.stroke}
                    strokeWidth={isSel ? 0.35 : 0.22}
                    vectorEffect="non-scaling-stroke"
                    style={{ transition: "fill 180ms ease" }}
                  />
                  <text
                    x={cx}
                    y={cy}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize={1.4}
                    fontWeight={700}
                    fill="#1A1A1A"
                    stroke="rgba(255,255,255,0.85)"
                    strokeWidth={0.15}
                    paintOrder="stroke"
                    style={{ pointerEvents: "none", userSelect: "none" }}
                  >
                    {h.unit_id}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>

        {/* Floating tooltip that follows cursor */}
        {tip.show && tip.unit && (
          <div
            className="absolute z-30 px-3 py-2 bg-white/95 dark:bg-[#1A1D24]/95 backdrop-blur border border-[#E5E5E5] dark:border-[#272A30] shadow-xl pointer-events-none rounded-sm text-xs"
            style={{
              left: Math.min(tip.x + 14, (viewportRef.current?.offsetWidth ?? 0) - 200),
              top:  Math.max(tip.y - 60, 8),
              minWidth: 170,
            }}
            data-testid="hotspot-tooltip"
          >
            <div className="font-semibold text-[#1A1A1A] dark:text-[#EAEAEA] text-sm mb-0.5">
              {tip.unit}
            </div>
            <div className="flex items-center gap-1.5 text-[#737373]">
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: (STATUS_COLORS[getStatus(tip.unit)] || STATUS_COLORS.missing).dot }}
              />
              <span>{(STATUS_COLORS[getStatus(tip.unit)] || STATUS_COLORS.missing).label}</span>
              <span className="mx-1">·</span>
              <span>{getSqFt(tip.unit)}</span>
            </div>
            <div className="text-[#737373] mt-0.5 truncate">
              Owner: <span className="text-[#1A1A1A] dark:text-[#EAEAEA]">{getOwnerName(tip.unit)}</span>
            </div>
          </div>
        )}

        {/* Reference plan thumbnail (collapsible) */}
        {floorPlanUrl && (
          refCollapsed ? (
            <button
              data-testid="reference-plan-show-btn"
              onClick={() => setRefCollapsed(false)}
              className="absolute top-3 left-3 z-20 flex items-center gap-1.5 px-2.5 py-1.5 bg-white/95 dark:bg-[#1A1D24]/95 backdrop-blur border border-[#E5E5E5] dark:border-[#272A30] shadow-lg rounded-sm text-[10px] tracking-wide text-[#737373] hover:text-[#9C7C54] transition-colors"
              title="Show reference plan"
            >
              <ImageIcon size={12} /> Reference Plan <ChevronRight size={10} />
            </button>
          ) : (
            <div
              className="absolute top-3 left-3 z-20 bg-white/95 dark:bg-[#1A1D24]/95 backdrop-blur border border-[#E5E5E5] dark:border-[#272A30] shadow-lg rounded-sm overflow-hidden w-[120px] sm:w-[180px]"
              data-testid="reference-plan-widget"
            >
              <div className="flex items-center justify-between px-2 py-1 border-b border-[#E5E5E5] dark:border-[#272A30]">
                <div className="flex items-center gap-1 text-[9px] tracking-[0.2em] uppercase font-semibold text-[#9C7C54]">
                  <ImageIcon size={9} /> Reference
                </div>
                <div className="flex items-center gap-0.5">
                  <button
                    data-testid="reference-plan-expand-btn"
                    onClick={() => setRefExpanded(true)}
                    className="w-5 h-5 flex items-center justify-center text-[#737373] hover:text-[#9C7C54] transition-colors"
                    title="Enlarge"
                  >
                    <Maximize2 size={10} />
                  </button>
                  <button
                    data-testid="reference-plan-hide-btn"
                    onClick={() => setRefCollapsed(true)}
                    className="w-5 h-5 flex items-center justify-center text-[#737373] hover:text-[#9C7C54] transition-colors"
                    title="Hide"
                  >
                    <X size={11} />
                  </button>
                </div>
              </div>
              <button
                onClick={() => setRefExpanded(true)}
                data-testid="reference-plan-thumb-btn"
                className="block w-full bg-white dark:bg-[#0F1115] cursor-zoom-in"
                style={{ maxHeight: 260, overflow: "hidden" }}
              >
                <img
                  src={floorPlanUrl}
                  alt={`${floorLabel} — original plan`}
                  draggable={false}
                  style={{ display: "block", width: "100%", height: "auto", userSelect: "none" }}
                />
              </button>
              <div className="px-2 py-1 text-[9px] text-[#737373] text-center">Click to enlarge</div>
            </div>
          )
        )}

        {/* Floating control widget */}
        <div
          className="absolute top-3 right-3 z-20 flex flex-col gap-1 bg-white/95 dark:bg-[#1A1D24]/95 backdrop-blur border border-[#E5E5E5] dark:border-[#272A30] shadow-lg rounded-sm p-1"
          data-testid="map-controls"
        >
          <button
            data-testid="map-zoom-in-btn"
            onClick={handleZoomIn}
            className="w-11 h-11 sm:w-8 sm:h-8 flex items-center justify-center text-[#737373] hover:text-[#9C7C54] hover:bg-[#F9F8F6] dark:hover:bg-[#272A30] transition-colors rounded-sm"
            title="Zoom in"
            aria-label="Zoom in"
          >
            <Plus size={18} className="sm:w-3.5 sm:h-3.5" />
          </button>
          <button
            data-testid="map-zoom-out-btn"
            onClick={handleZoomOut}
            className="w-11 h-11 sm:w-8 sm:h-8 flex items-center justify-center text-[#737373] hover:text-[#9C7C54] hover:bg-[#F9F8F6] dark:hover:bg-[#272A30] transition-colors rounded-sm"
            title="Zoom out"
            aria-label="Zoom out"
          >
            <Minus size={18} className="sm:w-3.5 sm:h-3.5" />
          </button>
          <div className="h-px bg-[#E5E5E5] dark:bg-[#272A30] mx-1" />
          <button
            data-testid="map-reset-btn"
            onClick={handleReset}
            className="w-11 h-11 sm:w-8 sm:h-8 flex items-center justify-center text-[#737373] hover:text-[#9C7C54] hover:bg-[#F9F8F6] dark:hover:bg-[#272A30] transition-colors rounded-sm"
            title="Reset fit"
            aria-label="Reset zoom"
          >
            <Maximize2 size={16} className="sm:w-3 sm:h-3" />
          </button>
        </div>

        {/* Pan hint — desktop copy hidden on mobile; mobile shows touch hint */}
        <div className="hidden sm:flex absolute bottom-3 left-3 z-20 items-center gap-1.5 px-2.5 py-1 bg-white/70 dark:bg-[#1A1D24]/70 backdrop-blur border border-[#E5E5E5] dark:border-[#272A30] rounded-sm text-[10px] text-[#737373]">
          <Hand size={11} /> Drag to pan · ⌘ + scroll to zoom
        </div>
        <div className="sm:hidden absolute bottom-3 left-3 z-20 flex items-center gap-1.5 px-2.5 py-1 bg-white/80 dark:bg-[#1A1D24]/80 backdrop-blur border border-[#E5E5E5] dark:border-[#272A30] rounded-sm text-[10px] text-[#737373]">
          <Hand size={11} /> Drag · Pinch to zoom
        </div>

        {/* Scale readout */}
        <div
          className="absolute bottom-3 right-3 z-20 px-2.5 py-1 bg-white/70 dark:bg-[#1A1D24]/70 backdrop-blur border border-[#E5E5E5] dark:border-[#272A30] rounded-sm text-[10px] font-mono text-[#737373]"
          data-testid="map-scale-readout"
        >
          {Math.round(transform.scale * 100)}%
        </div>

        {/* Empty-state hint */}
        {hotspots.length === 0 && floorPlanUrl && (
          <div className="absolute inset-x-0 top-3 flex justify-center pointer-events-none z-10">
            <div className="px-4 py-2 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 text-amber-800 dark:text-amber-300 text-xs rounded-sm">
              No hotspot polygons authored for this floor yet — admins can draw them in the Hotspot Editor.
            </div>
          </div>
        )}
      </div>

      {/* Reference plan lightbox */}
      {refExpanded && floorPlanUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur flex items-center justify-center p-6"
          onClick={() => setRefExpanded(false)}
          data-testid="reference-plan-lightbox"
        >
          <button
            onClick={() => setRefExpanded(false)}
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center text-white/80 hover:text-white transition-colors rounded-sm border border-white/20 bg-black/30 backdrop-blur"
            data-testid="reference-plan-lightbox-close-btn"
            aria-label="Close reference"
          >
            <X size={18} />
          </button>
          <div className="absolute top-5 left-6 text-white/70 text-xs tracking-[0.2em] uppercase font-semibold">
            {floorLabel} · Reference Plan
          </div>
          <img
            src={floorPlanUrl}
            alt={`${floorLabel} — original plan enlarged`}
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: "min(92vw, 1100px)",
              maxHeight: "calc(100vh - 80px)",
              width: "auto",
              height: "auto",
              boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
            }}
          />
        </div>
      )}
    </div>
  );
}
